import React, { useState } from 'react';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

function toYearMonth(yyyyMm) {
  if (!yyyyMm) return null;
  const [y, m] = yyyyMm.split('-');
  return { year: Number(y), month: Number(m) };
}

function toStr(year, month) {
  return `${year}-${String(month).padStart(2, '0')}`;
}

function cmp(yyyyMmA, yyyyMmB) {
  if (!yyyyMmA || !yyyyMmB) return 0;
  return yyyyMmA < yyyyMmB ? -1 : yyyyMmA > yyyyMmB ? 1 : 0;
}

function MonthPanel({ title, year, onYearChange, selectedStart, selectedEnd, onMonthClick }) {
  return (
    <div className="ldp-panel">
      <div className="ldp-panel-title">{title}</div>
      <div className="ldp-year-nav">
        <button type="button" className="ldp-year-arrow" onClick={() => onYearChange(year - 1)} aria-label="Previous year">&#8249;</button>
        <span className="ldp-year-label">{year}</span>
        <button type="button" className="ldp-year-arrow" onClick={() => onYearChange(year + 1)} aria-label="Next year">&#8250;</button>
      </div>
      <div className="ldp-month-grid">
        {MONTHS.map((name, idx) => {
          const monthNum = idx + 1;
          const val = toStr(year, monthNum);
          const isStart = val === selectedStart;
          const isEnd = val === selectedEnd;
          const inRange =
            selectedStart && selectedEnd &&
            cmp(val, selectedStart) > 0 && cmp(val, selectedEnd) < 0;

          let cls = 'ldp-month-btn';
          if (isStart) cls += ' ldp-month-start';
          else if (isEnd) cls += ' ldp-month-end';
          else if (inRange) cls += ' ldp-month-in-range';

          return (
            <button
              key={name}
              type="button"
              className={cls}
              onClick={() => onMonthClick(val)}
            >
              {name}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const DateRangePicker = ({ value, onChange, onClose }) => {
  const startParsed = toYearMonth(value.start);
  const endParsed = toYearMonth(value.end);

  const now = new Date();
  const [startYear, setStartYear] = useState(startParsed?.year ?? now.getFullYear());
  const [endYear, setEndYear] = useState(endParsed?.year ?? now.getFullYear());

  // Local draft — only committed on Apply
  const [draft, setDraft] = useState({ start: value.start, end: value.end });
  // Which field to fill next: 'start' | 'end'
  const [filling, setFilling] = useState('start');

  function handleMonthClick(val) {
    if (filling === 'start') {
      // If new start is after existing end, clear end too
      const newEnd = draft.end && cmp(val, draft.end) > 0 ? null : draft.end;
      setDraft({ start: val, end: newEnd });
      setFilling('end');
    } else {
      // If new end is before start, swap or reject
      if (draft.start && cmp(val, draft.start) < 0) {
        setDraft({ start: val, end: draft.start });
      } else {
        setDraft((d) => ({ ...d, end: val }));
      }
      setFilling('start');
    }
  }

  function handleApply() {
    onChange(draft);
    onClose();
  }

  function handleClear() {
    const cleared = { start: null, end: null };
    setDraft(cleared);
    onChange(cleared);
    onClose();
  }

  const canApply = Boolean(draft.start && draft.end);

  return (
    <div className="ldp-dropdown" role="dialog" aria-label="Select date range">
      <div className="ldp-panels">
        <MonthPanel
          title="From"
          year={startYear}
          onYearChange={setStartYear}
          selectedStart={draft.start}
          selectedEnd={draft.end}
          onMonthClick={handleMonthClick}
        />
        <div className="ldp-divider" aria-hidden="true" />
        <MonthPanel
          title="To"
          year={endYear}
          onYearChange={setEndYear}
          selectedStart={draft.start}
          selectedEnd={draft.end}
          onMonthClick={handleMonthClick}
        />
      </div>
      <div className="ldp-hint">
        {filling === 'start' ? 'Select start month' : 'Select end month'}
      </div>
      <div className="ldp-actions">
        <button type="button" className="ldp-btn-clear" onClick={handleClear}>Clear</button>
        <button type="button" className="ldp-btn-apply" onClick={handleApply} disabled={!canApply}>Apply</button>
      </div>
    </div>
  );
};

export default DateRangePicker;
